<template>
  <div v-if="hasCallOptions" class="call-via-dropdown">
    <button
      type="button"
      class="call-via-trigger"
      :class="{
        'is-open': showDropdown,
        'is-busy': isBusy,
        'call-via-trigger--icon-only': iconOnly,
      }"
      :disabled="isBusy"
      v-tooltip.top-end="
        iconOnly ? $t('CONVERSATION.REPLYBOX.CALL') : null
      "
      @click="toggleDropdown"
    >
      <fluent-icon
        :icon="triggerIcon"
        :size="iconOnly ? 14 : 16"
        :view-box="iconOnly ? '0 0 15.681 15.684' : '0 0 24 24'"
        class="call-via-trigger__icon"
        :class="{ 'call-via-trigger__icon--spin': isBusy }"
      />
      <span v-if="!iconOnly" class="call-via-trigger__label">{{
        $t('CONVERSATION.REPLYBOX.CALL')
      }}</span>
      <fluent-icon
        v-if="!iconOnly"
        :icon="showDropdown ? 'chevron-down' : 'chevron-up'"
        size="14"
        class="call-via-trigger__chevron"
      />
    </button>

    <transition name="fade">
      <div
        v-if="showDropdown"
        v-on-clickaway="closeDropdown"
        class="call-via-menu"
        :class="{
          'call-via-menu--below': menuOpensBelow,
          'call-via-menu--align-end': menuAlignEnd,
        }"
      >
        <div class="call-via-menu__header">
          <span class="call-via-menu__header-title">
            {{ $t('CONVERSATION.REPLYBOX.CALL_VIA') }}
          </span>
          <span
            v-if="agentPhoneNumber"
            class="call-via-menu__header-agent-phone"
            :title="$t('CONVERSATION.REPLYBOX.AGENT_PHONE_NUMBER')"
          >
            {{ agentPhoneNumber }}
          </span>
        </div>

        <div
          v-if="showInternationalCall"
          class="call-via-menu__section"
        >
          <button
            type="button"
            class="call-via-menu__item"
            :disabled="isBusy"
            @click="handleInternationalCall"
          >
            <span class="call-via-menu__logo call-via-menu__logo--twilio">
              <img
                :src="twilioLogoPath"
                alt="Twilio"
                class="call-via-menu__logo-image"
              />
            </span>
            <span class="call-via-menu__details">
              <span class="call-via-menu__name">
                {{ $t('CONVERSATION.REPLYBOX.INTERNATIONAL_CALL') }}
              </span>
              <span v-if="twilioPhoneNumber" class="call-via-menu__phone">
                {{ twilioPhoneNumber }}
              </span>
            </span>
          </button>
        </div>

        <div
          v-for="(group, groupIndex) in groupedInboxes"
          :key="group.key"
          class="call-via-menu__section"
        >
          <div
            v-if="showInternationalCall || groupIndex > 0"
            class="call-via-menu__divider"
          />

          <button
            v-for="inbox in group.inboxes"
            :key="inbox.value"
            type="button"
            class="call-via-menu__item"
            :disabled="isBusy"
            @click="handleDomesticCall(inbox.value)"
          >
            <span
              class="call-via-menu__logo"
              :class="logoClass(inbox.inboxObject.channel_type)"
            >
              <fluent-icon
                v-if="isJioEvaChannel(inbox.inboxObject.channel_type)"
                icon="jio"
                size="18"
                class="call-via-menu__logo-icon call-via-menu__logo-icon--jio"
              />
              <img
                v-else-if="isSmartfloChannel(inbox.inboxObject.channel_type)"
                :src="smartfloLogoPath"
                alt="Tata Tele"
                class="call-via-menu__logo-image"
              />
            </span>
            <span class="call-via-menu__details">
              <span class="call-via-menu__name">{{ inbox.label }}</span>
              <span v-if="displayPhone(inbox)" class="call-via-menu__phone">
                {{ displayPhone(inbox) }}
              </span>
            </span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';
import {
  formatDomesticPhoneNumber,
  groupDomesticInboxesByProvider,
  isJioEvaChannel,
  isSmartfloChannel,
  domesticCallButtonClass,
  SMARTFLO_LOGO_PATH,
  TWILIO_LOGO_PATH,
} from 'dashboard/helper/domesticCallProviders';

export default {
  name: 'CallViaDropdown',
  mixins: [clickaway],
  props: {
    domesticInboxes: {
      type: Array,
      default: () => [],
    },
    showInternationalCall: {
      type: Boolean,
      default: false,
    },
    menuOpensBelow: {
      type: Boolean,
      default: false,
    },
    menuAlignEnd: {
      type: Boolean,
      default: false,
    },
    isBusy: {
      type: Boolean,
      default: false,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDropdown: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      agents: 'agents/getAgents',
    }),
    groupedInboxes() {
      return groupDomesticInboxesByProvider(this.domesticInboxes);
    },
    hasCallOptions() {
      return this.showInternationalCall || this.domesticInboxes.length > 0;
    },
    triggerIcon() {
      if (this.isBusy) return 'spinner';

      return this.iconOnly ? 'contact-call' : 'call-start';
    },
    currentAgentRecord() {
      return this.agents.find(
        agent => Number(agent.id) === Number(this.currentUser?.id)
      );
    },
    agentPhoneNumber() {
      const phone =
        this.currentAgentRecord?.phone_number || this.currentUser?.phone_number;
      if (!phone) return '';

      return formatDomesticPhoneNumber(phone);
    },
    twilioPhoneNumber() {
      const phone = this.currentAgentRecord?.twilio_number;
      if (!phone) return '';

      return formatDomesticPhoneNumber(phone);
    },
    smartfloLogoPath() {
      return SMARTFLO_LOGO_PATH;
    },
    twilioLogoPath() {
      return TWILIO_LOGO_PATH;
    },
  },
  mounted() {
    if (!this.agents.length) {
      this.$store.dispatch('agents/get');
    }
  },
  methods: {
    isJioEvaChannel,
    isSmartfloChannel,
    domesticCallButtonClass,
    toggleDropdown() {
      if (this.isBusy) return;

      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.$store.dispatch('agents/get');
      }
    },
    closeDropdown() {
      this.showDropdown = false;
    },
    displayPhone(inbox) {
      const callerId = inbox.inboxObject?.caller_id || inbox.caller_id;
      if (!callerId) return '';

      return formatDomesticPhoneNumber(callerId);
    },
    providerBadgeClass(channelType) {
      return this.domesticCallButtonClass(channelType);
    },
    logoClass(channelType) {
      if (this.isJioEvaChannel(channelType)) {
        return ['call-via-menu__logo--jio', this.providerBadgeClass(channelType)];
      }

      if (this.isSmartfloChannel(channelType)) {
        return ['call-via-menu__logo--smartflo', this.providerBadgeClass(channelType)];
      }

      return this.providerBadgeClass(channelType);
    },
    handleDomesticCall(inboxId) {
      if (this.isBusy) return;

      this.closeDropdown();
      this.$emit('domestic-call', inboxId);
    },
    handleInternationalCall() {
      if (this.isBusy) return;

      this.closeDropdown();
      this.$emit('international-call');
    },
  },
};
</script>

<style lang="scss" scoped>
.call-via-dropdown {
  position: relative;
  display: inline-flex;
}

.call-via-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2rem;
  padding: 0 0.625rem;
  border: none;
  border-radius: 0.375rem;
  background: var(--w-500);
  color: var(--white);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled),
  &.is-open:not(:disabled) {
    background: var(--w-600);
  }

  &:disabled,
  &.is-busy {
    cursor: wait;
    opacity: 0.85;
  }

  &__icon {
    flex-shrink: 0;

    &--spin {
      animation: call-via-trigger-spin 0.8s linear infinite;
    }
  }

  &__label {
    line-height: 1;
  }

  &__chevron {
    flex-shrink: 0;
  }

  &--icon-only {
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0;
    margin: 0;
    gap: 0;
    line-height: 0;
    justify-content: center;
    align-items: center;
    border-radius: 8px;

    .call-via-trigger__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 0;
      margin: 0;

      :deep(svg) {
        display: block;
      }
    }
  }
}

.call-via-menu {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  /* Below sidebar panels (Primary: 33, Secondary: 30) */
  z-index: 24;

  &--below {
    top: calc(100% + 0.5rem);
    bottom: auto;
  }

  &--align-end {
    left: auto;
    right: 0;
  }

  width: 16.5rem;
  max-height: 24rem;
  overflow-y: auto;
  background: var(--white);
  border: 1px solid var(--s-75);
  border-radius: 0.625rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);

  :global(body.dark) & {
    background: var(--s-900);
    border-color: var(--s-700);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 0.875rem 0.5rem;
  }

  &__header-title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--s-900);

    :global(body.dark) & {
      color: var(--s-50);
    }
  }

  &__header-agent-phone {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--s-500);
    white-space: nowrap;

    :global(body.dark) & {
      color: var(--s-400);
    }
  }

  &__section {
    padding: 0 0.5rem 0.25rem;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.5rem 0.625rem;
    border: none;
    border-radius: 0.375rem;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;

    &:disabled {
      cursor: wait;
      opacity: 0.6;
    }

    &:hover:not(:disabled) {
      background: var(--s-50);

      :global(body.dark) & {
        background: var(--s-800);
      }
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 9999px;
    overflow: hidden;

    &--jio {
      color: var(--white);
      background-color: #3d3dc9;
    }

    &--smartflo,
    &--twilio {
      background: var(--white);
      border: 1px solid var(--s-100);
      padding: 0.1875rem;

      :global(body.dark) & {
        background: var(--white);
        border-color: var(--s-300);
      }
    }
  }

  &__logo-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;

    :deep(svg) {
      display: block;
    }

    &--jio {
      width: 1.375rem;
      height: 1.375rem;

      :deep(svg) {
        width: 100%;
        height: 100%;
      }
    }
  }

  &__logo-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__details {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
  }

  &__name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--s-900);
    line-height: 1.25;

    :global(body.dark) & {
      color: var(--s-50);
    }
  }

  &__phone {
    margin-top: 0.125rem;
    font-size: 0.75rem;
    color: var(--s-500);
    line-height: 1.25;

    :global(body.dark) & {
      color: var(--s-400);
    }
  }

  &__divider {
    height: 1px;
    margin: 0.25rem 0.75rem;
    background: var(--s-75);

    :global(body.dark) & {
      background: var(--s-700);
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(0.25rem);
}

@keyframes call-via-trigger-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
